(() => {
  const setAccordion = (band, open) => {
    band?.classList.toggle('is-collapsed', !open);
    band?.querySelector('.main-product__accordion-trigger')?.setAttribute('aria-expanded', open);
  };

  const initAccordions = (root) => {
    ['.main-product__reviews', '.main-product__trust', '.main-product__customer-reviews', '.main-product__protection', '.main-product__fabric'].forEach((selector) => {
      const band = root.querySelector(selector);
      const trigger = band?.querySelector('.main-product__accordion-trigger');
      if (!band || !trigger) return;
      trigger.addEventListener('click', () => {
        band.classList.toggle('is-collapsed');
        trigger.setAttribute('aria-expanded', String(!band.classList.contains('is-collapsed')));
      });
    });

    const reviews = root.querySelector('.main-product__reviews');
    const openReviews = () => {
      setAccordion(reviews, true);
      reviews?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    root.querySelector('.main-product__rating')?.addEventListener('click', openReviews);
    root.querySelector('.main-product__share')?.addEventListener('click', openReviews);

    if (!matchMedia('(min-width: 990px) and (max-width: 1199px)').matches) return;
    setAccordion(reviews, true);
    ['.main-product__trust', '.main-product__protection', '.main-product__fabric'].forEach((selector) => setAccordion(root.querySelector(selector), false));
  };

  const initFeatures = (root) => {
    root.querySelectorAll('.main-product__story').forEach((story) => {
      const track = story.querySelector('.main-product__story-track');
      const arrows = story.querySelectorAll('.main-product__story-arrows button');
      if (!track || arrows.length < 2) return;
      const [prev, next] = arrows;
      const cardStep = 350;
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -cardStep, behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: cardStep, behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      if (window.matchMedia('(min-width: 990px)').matches) {
        arrows.forEach((arrow) => {
          arrow.tabIndex = 0;
          arrow.parentElement?.removeAttribute('aria-hidden');
        });
      }
      update();
    });
  };

  const initReviews = (root) => {
    root.querySelectorAll('[data-yx-review-slider]').forEach((slider) => {
      const cards = Array.from(slider.querySelectorAll('[data-yx-review-card]'));
      const count = slider.querySelector('[data-yx-review-count]');
      const prev = slider.querySelector('[data-yx-review-prev]');
      const next = slider.querySelector('[data-yx-review-next]');
      const usesMobilePager = window.matchMedia('(max-width: 749px)').matches;
      const usesTabletPager = window.matchMedia('(min-width: 990px) and (max-width: 1199px)').matches;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = 0;
      const scrollSelectedCard = () => {
        if (usesTabletPager) return;
        cards[index]?.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'nearest', inline: 'nearest' });
      };
      const render = () => {
        if (count) count.textContent = `${index + 1}/${cards.length}`;
        if (usesMobilePager) {
          if (prev) prev.disabled = index === 0;
          if (next) next.disabled = index === cards.length - 1;
        }
        if (usesTabletPager) cards.forEach((card, cardIndex) => { card.hidden = cardIndex !== index; });
      };
      prev?.addEventListener('click', () => {
        index = usesMobilePager ? Math.max(0, index - 1) : (index - 1 + cards.length) % cards.length;
        render();
        scrollSelectedCard();
      });
      next?.addEventListener('click', () => {
        index = usesMobilePager ? Math.min(cards.length - 1, index + 1) : (index + 1) % cards.length;
        render();
        scrollSelectedCard();
      });
      render();
    });
  };

  const initWatched = (root) => {
    const watchedSection = root.querySelector('[data-yx-watched]');
    const watchedTrack = watchedSection?.querySelector('[data-yx-watched-track]');
    const currentHandle = root.dataset.yxProductHandle || '';
    let history = [];
    try { history = JSON.parse(window.localStorage.getItem('yx-watched-products') || '[]'); } catch (_) { history = []; }
    const others = history.filter((handle) => typeof handle === 'string' && handle && handle !== currentHandle).slice(0, 5);
    try { window.localStorage.setItem('yx-watched-products', JSON.stringify([currentHandle, ...history.filter((handle) => handle && handle !== currentHandle)].filter(Boolean).slice(0, 10))); } catch (_) {}
    if (!watchedSection || !watchedTrack || !others.length) return;
    Promise.all(others.map(async (handle) => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}?section_id=yx-watched-card`);
        if (!response.ok) return null;
        const card = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-yx-product-card]');
        return card?.outerHTML || null;
      } catch (_) { return null; }
    })).then((cards) => {
      const valid = cards.filter(Boolean);
      if (!valid.length) return;
      watchedTrack.innerHTML = valid.map((card) => `<li>${card}</li>`).join('');
      watchedSection.hidden = false;
      root.dispatchEvent(new CustomEvent('yx:pdp-related-updated'));
    });
  };

  const initTabletTrust = (root) => {
    const reviews = root.querySelector('.main-product__reviews');
    const customerReviews = root.querySelector('.main-product__customer-reviews');
    const trust = root.querySelector('.main-product__trust');
    const tablet = window.matchMedia('(min-width: 750px) and (max-width: 1199px)');
    const lockedTablet = window.matchMedia('(min-width: 990px) and (max-width: 1199px)');
    if (!reviews) return;

    const syncPlacement = () => {
      const carousel = tablet.matches && customerReviews ? customerReviews : (lockedTablet.matches ? trust : null);

      [customerReviews, trust].forEach((band) => {
        if (!band || band === carousel) return;
        delete band.dataset.yxTabletEmbedded;
        band.style.removeProperty('order');
      });

      if (!carousel) return;
      carousel.dataset.yxTabletEmbedded = 'true';
      carousel.style.setProperty('order', '0');
      if (carousel.previousElementSibling !== reviews) reviews.insertAdjacentElement('afterend', carousel);
    };

    syncPlacement();
    tablet.addEventListener?.('change', syncPlacement);
    lockedTablet.addEventListener?.('change', syncPlacement);
    document.addEventListener('yx:customer-reviews-ready', syncPlacement);
  };

  const initFullDescription = (root) => {
    const description = root.querySelector('.main-product__description');
    const purchase = root.querySelector('.main-product__purchase');
    if (!description || !purchase) return;

    const features = root.querySelector('.main-product__feature-icons--meta');
    const denseFeatures = features?.querySelector('li:nth-child(13)');

    const desktop = window.matchMedia('(min-width: 1200px)');
    const syncDesktopSpace = () => {
      if (!desktop.matches) {
        root.style.removeProperty('--yx-pdp-description-extra');
        if (denseFeatures) description.style.removeProperty('top');
        return;
      }

      window.requestAnimationFrame(() => {
        // Preserve the locked one/two-row layout. Larger real feature lists
        // need enough space for every row before the native description.
        if (denseFeatures) {
          description.style.removeProperty('top');
          const baselineTop = Number.parseFloat(getComputedStyle(description).top) || 0;
          const featuresBottom = features.offsetTop + features.getBoundingClientRect().height;
          description.style.top = `${Math.max(baselineTop, featuresBottom + 32)}px`;
        }
        const currentExtra = Number.parseFloat(root.style.getPropertyValue('--yx-pdp-description-extra')) || 0;
        const descriptionBottom = description.getBoundingClientRect().bottom;
        const purchaseBottom = purchase.getBoundingClientRect().bottom - currentExtra;
        const extra = Math.max(0, Math.ceil(descriptionBottom - purchaseBottom + 60));
        if (extra !== currentExtra) root.style.setProperty('--yx-pdp-description-extra', `${extra}px`);
      });
    };

    syncDesktopSpace();
    desktop.addEventListener?.('change', syncDesktopSpace);
    window.addEventListener('resize', syncDesktopSpace, { passive: true });
    document.fonts?.ready.then(syncDesktopSpace);
    new ResizeObserver(syncDesktopSpace).observe(description);
    if (denseFeatures) new ResizeObserver(syncDesktopSpace).observe(features);
  };

  const init = (root) => {
    if (!root || root.dataset.yxContentBandsReady === 'true') return;
    root.dataset.yxContentBandsReady = 'true';
    initAccordions(root);
    initFeatures(root);
    initReviews(root);
    initWatched(root);
    initTabletTrust(root);
    initFullDescription(root);
  };

  window.YurinoxPdpContentBands = { init };
  document.querySelectorAll('[data-yx-product-root]').forEach(init);
})();
